/**
 * Excel de Lista de Raya.
 *
 * Salio de `TablaPrenomina.descargarListaRaya`, donde vivia como un metodo de 647
 * lineas con los estilos, el encabezado y las cuatro hojas en linea. Se extrajo sin
 * cambiar la logica para que lo puedan usar los dos consumidores:
 *
 *   · la pantalla de la prenomina, con UNA prenomina (el boton de siempre);
 *   · el reporte de Reportes de Nomina, con VARIAS prenominas seleccionadas.
 *
 * El catalogo de conceptos —que decide las columnas— se arma sobre las lineas de
 * TODAS las prenominas, no de cada una: asi todas las hojas comparten el mismo
 * juego de columnas y el Concentrado global las puede sumar. Con una sola prenomina
 * el resultado es identico al de antes.
 *
 * Construir y guardar van separados a proposito: `construirLibroListaRaya` es pura
 * (solo xlsx-js-style) y se puede correr fuera del navegador para verificarla;
 * `descargarListaRaya` es la que toca `fetch`, JSZip y `saveAs`.
 *
 * OJO: xlsx-js-style ignora !freeze / !panes (es funcion de SheetJS Pro), asi que no
 * hay forma de dejar los encabezados fijos al hacer scroll.
 */

import * as XLSX from 'xlsx-js-style'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import { FORMATO_CONTABILIDAD } from '../../../Components/ExcelEstilos'
import logoHorizontal from '../../../assets/Biwo_Horizontal.png'
import {
  CUBETA_FALTAS, anclarDesdeUtc, construirDiasPeriodo, marcasDiaDeLinea, rejillaDeLinea, resumenDiasDeLinea,
} from './incidenciasListaRaya'

dayjs.locale('es')

// ─── Paleta ───────────────────────────────────────────────────────────────────
// Los colores del logo de BIWO (src/Icons/IconBiwo.js) y los neutros del reporte de
// presentacion de Tesoreria (src/Pdf), que es el que marca el estilo "moderno".
const TURQUESA = '00BFD6'   // turquesa de marca
const NARANJA  = 'FF6A1A'   // el acento naranja del logo
const TEAL_OSC = '007A8A'
const TEAL_PRO = '005F6B'
const BANDA    = 'DFF5F9'   // banda de total por departamento
const BANDA_GR = 'BFEDF5'   // banda del total general
const ZEBRA    = 'F5FBFC'
const TINTA    = '262626'   // negro suave, no #000
const TINTA_SU = '8C8C8C'
const ROJO     = 'E53935'
const GRIS     = '9E9E9E'

// Calibri en lugar de Arial 8: es la tipografica nativa de Excel y se lee mejor a
// tamanos chicos. La jerarquia va por tamano y peso, no por adornos.
const ca = (ex = {}) => ({ name: 'Calibri', sz: 9, color: { rgb: TINTA }, ...ex })

// ─── Estilos ──────────────────────────────────────────────────────────────────
// Bloque de encabezado. El logotipo se arma con los dos colores de la marca:
// BIWO en turquesa grande, NOMINAS en naranja chico debajo.
const sBiwo    = { font: ca({ sz: 20, bold: true, color: { rgb: TURQUESA } }), alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }
const sNominas = { font: ca({ sz: 9,  bold: true, color: { rgb: NARANJA } }),  alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }
const sEmpresa = { font: ca({ sz: 14, bold: true }), alignment: { horizontal: 'left', vertical: 'center' } }
const sTitulo  = { font: ca({ sz: 11, color: { rgb: TINTA_SU } }), alignment: { horizontal: 'left', vertical: 'center' } }
const sMeta    = { font: ca({ color: { rgb: TINTA_SU } }), alignment: { horizontal: 'left', vertical: 'center' } }
// Regla de marca: la unica mancha de color fuerte del encabezado
const sRegla   = { fill: { fgColor: { rgb: TURQUESA } } }

// Fila de encabezados: relleno turquesa y solo una regla abajo — sin recuadros. Las
// columnas derivadas (los totales) van en el turquesa oscuro, porque esa diferencia
// dice algo real: no se capturan, se calculan.
const hdr = (fondo = TURQUESA) => ({
  fill: { fgColor: { rgb: fondo } },
  font: ca({ bold: true, color: { rgb: 'FFFFFF' } }),
  border: { bottom: { style: 'medium', color: { rgb: TEAL_PRO } } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
})
const hdrTxt = (fondo = TURQUESA) => ({
  ...hdr(fondo),
  alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
})

// Banners de division
const sRegPat   = { font: ca({ sz: 10, bold: true, color: { rgb: TEAL_OSC } }), numFmt: '@' }
const sDepto    = { fill: { fgColor: { rgb: BANDA } }, font: ca({ bold: true, color: { rgb: TEAL_PRO } }), alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }
const sDeptoRel = { fill: { fgColor: { rgb: BANDA } } }

// Todo importe negativo va en rojo, en cualquier renglon. El rojo gana sobre el gris
// de las informativas: primero se ve que es negativo.
const colorImporte = (v, info) => v < 0 ? ROJO : (info ? GRIS : null)
const relleno = (rgb) => rgb ? { fill: { fgColor: { rgb } } } : {}

// Detalle del empleado — zebra suave en lugar de bordes por celda
const sCod = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'left' }, numFmt: '@' })
const sNom = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null) })
const sImp = (v, info, impar) => ({
  font: ca(colorImporte(v, info) ? { color: { rgb: colorImporte(v, info) } } : {}),
  ...relleno(impar ? ZEBRA : null),
  alignment: { horizontal: 'right' },
  numFmt: FORMATO_CONTABILIDAD,
})
const sEnteros = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'center' }, numFmt: '#,##0' })
const sNomDepto = (impar) => ({ font: ca({ bold: true }), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'left' } })
const sFecha = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'center' }, numFmt: '@' })

// ─── Bloque de incidencias ────────────────────────────────────────────────────
// Una columna por dia del periodo. El encabezado lleva el dia del mes y el dia de la semana en
// dos renglones, para no necesitar una fila extra: el bloque de encabezado mide 10 filas fijas y
// su ultima ya tiene altura para dos lineas (ver ALTOS_ENCABEZADO).
//
// El septimo dia se marca en el turquesa oscuro, igual que en la rejilla de la pantalla: una
// incidencia capturada ahi no descuenta sueldo ni septimo dia, pero SI cuenta para el ausentismo
// de IMSS, asi que se senala en vez de esconderse.
const hdrDia = (esSeptimo) => ({
  ...hdr(esSeptimo ? TEAL_OSC : TURQUESA),
  font: ca({ sz: 8, bold: true, color: { rgb: 'FFFFFF' } }),
})

// Marcador del dia. Las faltas en rojo porque son las que descuentan y las que suma el TOTAL;
// lo demas (vacaciones, incapacidades, retardos) en gris, que son informacion de contexto.
const sDia = (impar, cubeta) => ({
  font: ca(cubeta === CUBETA_FALTAS ? { bold: true, color: { rgb: ROJO } } : { color: { rgb: TINTA_SU } }),
  ...relleno(impar ? ZEBRA : null),
  alignment: { horizontal: 'center', vertical: 'center' },
  numFmt: '@',
})
// El marcador de falta va como NUMERO —no como texto— para que el TOTAL de la columna signifique
// algo y para que Excel lo pueda sumar si el usuario agrega una formula suya.
const sDiaNum = (impar) => ({ ...sDia(impar, CUBETA_FALTAS), numFmt: '0.##' })
// Celda de observaciones: se deja vacia y con un borde suave, porque es de captura a mano.
const sObs = (impar) => ({
  font: ca(),
  ...relleno(impar ? ZEBRA : null),
  border: { bottom: { style: 'hair', color: { rgb: TINTA_SU } } },
  alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
  numFmt: '@',
})
// Leyenda del bloque, en la fila de aire del encabezado.
const sLeyenda = { font: ca({ sz: 8, italic: true, color: { rgb: TINTA_SU } }), alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }

// Total por departamento: un solo renglon con banda y regla arriba. Sustituye al
// viejo par de renglones de guiones, que era un resabio de impresora de punto.
const bordeTot = { top: { style: 'thin', color: { rgb: TURQUESA } } }
const sTotLbl = { fill: { fgColor: { rgb: BANDA } }, font: ca({ bold: true, color: { rgb: TEAL_PRO } }), border: bordeTot, alignment: { horizontal: 'left' }, numFmt: '@' }
const sTotRel = { fill: { fgColor: { rgb: BANDA } }, border: bordeTot }
const sTotNum = (v, info) => ({
  fill: { fgColor: { rgb: BANDA } },
  font: ca({ bold: true, color: { rgb: colorImporte(v, info) || TEAL_PRO } }),
  border: bordeTot,
  alignment: { horizontal: 'right' },
  numFmt: FORMATO_CONTABILIDAD,
})
const sTotEnt = { ...sTotNum(0, false), numFmt: '#,##0', alignment: { horizontal: 'center' } }

// Total general: la misma banda un tono mas fuerte y una regla mas gruesa. Al ser
// clara, los negativos pueden ir en rojo de verdad.
const bordeGT = { top: { style: 'medium', color: { rgb: TEAL_OSC } } }
const sGTLbl = { fill: { fgColor: { rgb: BANDA_GR } }, font: ca({ sz: 10, bold: true, color: { rgb: TEAL_PRO } }), border: bordeGT, alignment: { horizontal: 'left' }, numFmt: '@' }
const sGTRel = { fill: { fgColor: { rgb: BANDA_GR } }, border: bordeGT }
const sGTNum = (v, info) => ({
  fill: { fgColor: { rgb: BANDA_GR } },
  font: ca({ sz: 10, bold: true, color: { rgb: colorImporte(v, info) || TEAL_PRO } }),
  border: bordeGT,
  alignment: { horizontal: 'right' },
  numFmt: FORMATO_CONTABILIDAD,
})
const sGTEnt = { ...sGTNum(0, false), numFmt: '#,##0', alignment: { horizontal: 'center' } }

// Helpers de celda. `cn` fuerza t:'n' — sin el, Excel escribe el numero como texto.
const cs = (v, s) => ({ v: v || '', t: 's', s })
const cn = (v, s) => ({ v: typeof v === 'number' ? v : 0, t: 'n', s })
const ce = (s)    => ({ v: '', t: 's', s })

// ─── Encabezado ───────────────────────────────────────────────────────────────
// Las filas 1-2 se dejan libres para que ahi flote el logo; si el logo no se pudo
// cargar caen a texto. La fila 3 es aire: el logo trae su lema abajo y sin ese
// margen el nombre de la empresa se le encimaba. Debajo van empresa, titulo,
// periodo, datos fiscales y la regla de marca, y los encabezados de tabla quedan en
// la fila 10.
//
// Las filas 1-2 miden 52pt (~69 px) contra los 58 px que ocupa el logo con su
// desplazamiento, asi que sobra holgura; ver xmlDrawingLogo().
const ALTOS_ENCABEZADO = [
  { hpt: 26 }, { hpt: 26 }, { hpt: 8 },                // logo + aire
  { hpt: 19 }, { hpt: 15 }, { hpt: 14 }, { hpt: 14 },  // empresa / titulo / periodo / fiscal
  { hpt: 4 },  { hpt: 7 },  { hpt: 34 },               // regla / aire / encabezados
]

// OJO: una fila sin celdas no genera <row> en el XML y pierde su alto (SheetJS se
// salta las filas vacias iniciales). Las filas que solo existen por su altura —las
// del logo y la de aire— tienen que llevar una celda vacia o el logo se desborda
// sobre el texto.
const filaSoloAlto = () => [ce(sMeta)]

/**
 * Devuelve la funcion que escribe el bloque de encabezado de una hoja.
 *
 * `leyenda` (opcional) se escribe en la fila de aire, la novena. Va ahi y no en una fila nueva
 * porque el bloque mide 10 filas fijas (ALTOS_ENCABEZADO) y los encabezados de tabla tienen que
 * seguir cayendo en la decima. Quien la use debe subirle el alto a esa fila — 7pt no alcanzan.
 */
const crearEncabezado = ({ conLogo, empresaNombre, periodoTxt, metaFiscal }) =>
  (filas, merges, subtitulo, ultCol, leyenda) => {
    if (conLogo) {
      filas.push(filaSoloAlto())
      filas.push(filaSoloAlto())
    } else {
      filas.push([cs('BIWO', sBiwo)])
      filas.push([cs('NÓMINAS', sNominas)])
    }
    filas.push(filaSoloAlto())                                        // aire bajo el logo
    filas.push([cs(empresaNombre || '', sEmpresa)])
    filas.push([cs(subtitulo, sTitulo)])
    filas.push([cs(periodoTxt, sMeta)])
    filas.push([cs(metaFiscal, sMeta)])
    for (let r = 3; r <= 6; r++) merges.push({ s: { r, c: 0 }, e: { r, c: ultCol } })
    filas.push(Array.from({ length: ultCol + 1 }, () => ce(sRegla)))  // regla de marca
    if (leyenda) {
      const fLey = filas.length
      filas.push([cs(leyenda, sLeyenda)])
      merges.push({ s: { r: fLey, c: 0 }, e: { r: fLey, c: ultCol } })
    } else {
      filas.push([])                                                  // aire
    }
  }

// ─── Lectura de importes ──────────────────────────────────────────────────────
const efectivo = c => {
  if (!c) return 0
  return (c.importeTotalManual !== null && c.importeTotalManual !== undefined)
    ? c.importeTotalManual : (c.importeTotal || 0)
}
const getVal = (linea, id) =>
  efectivo((linea.conceptos || []).find(c => c.conceptoNominaId === id))

const sumar = (destino, valores) => valores.forEach((v, i) => { destino[i] += v })

const porCodigo = (a, b) => {
  const ca_ = a.codigo == null ? Number.MAX_SAFE_INTEGER : Number(a.codigo)
  const cb_ = b.codigo == null ? Number.MAX_SAFE_INTEGER : Number(b.codigo)
  if (ca_ !== cb_) return ca_ - cb_
  return `${a.apellidoPaterno || ''} ${a.nombre || ''}`
    .localeCompare(`${b.apellidoPaterno || ''} ${b.nombre || ''}`)
}

/**
 * Catalogo de conceptos que define las columnas del reporte: tipo 1 → 2 → 3, y
 * dentro de cada tipo por serie-numero. Se descartan los conceptos que valen 0 en
 * todas las lineas.
 *
 * Conceptos informativos (noSuma): se muestran pero NO entran en los totales, igual
 * que en la tabla en pantalla, en el Sobre Recibo y en los totales del backend. Se
 * toma el catalogo como fuente de verdad y se cae al flag de la linea para los
 * conceptos agregados a mano.
 *
 * Percepciones en especie (especie): se muestran con su importe como cualquier otra
 * columna, pero NO entran en TOTAL PERCEPCIONES ni en el NETO, porque no se le entregan
 * en dinero al empleado. Comparten con los informativos el tratamiento numerico y el
 * atenuado —la columna existe y no suma—, pero no son lo mismo: una percepcion en especie
 * si se timbra y si grava.
 *
 * @param {Array} lineas     TODAS las lineas de TODAS las prenominas del libro
 * @param {Set}   noSumaIds  ids del catalogo marcados como informativos
 * @param {Set}   especieIds ids del catalogo marcados como percepcion en especie
 */
export function construirCatalogoConceptos(lineas, noSumaIds, especieIds) {
  const ids = noSumaIds || new Set()
  const idsEsp = especieIds || new Set()
  const esInfo = c => ids.has(c.conceptoNominaId?.toString()) || !!c.noSuma
  const esEspecie = c =>
    c.tipo === 1 && !esInfo(c) && (idsEsp.has(c.conceptoNominaId?.toString()) || !!c.especie)

  const cMap = new Map()
  ;(lineas || []).forEach(l =>
    (l.conceptos || []).forEach(c => {
      if (!cMap.has(c.conceptoNominaId))
        cMap.set(c.conceptoNominaId, {
          id: c.conceptoNominaId, descripcion: c.descripcion,
          tipo: c.tipo, serie: c.serie || '', numero: c.numero || '',
          noSuma: esInfo(c),
          especie: esEspecie(c),
        })
    })
  )
  const todos = Array.from(cMap.values()).sort((a, b) =>
    a.tipo !== b.tipo ? a.tipo - b.tipo
      : `${a.serie}-${a.numero}`.localeCompare(`${b.serie}-${b.numero}`)
  )
  const tieneValor = id => (lineas || []).some(l => getVal(l, id) !== 0)

  const percs = todos.filter(c => c.tipo === 1 && tieneValor(c.id))
  const dedus = todos.filter(c => c.tipo === 2 && tieneValor(c.id))
  const oblis = todos.filter(c => c.tipo === 3 && tieneValor(c.id))

  const IMP_COLS = percs.length + 1 + dedus.length + 1 + 1 + oblis.length + 1

  // Que columnas de importe quedan fuera de los totales de percepciones / deducciones /
  // neto / obligaciones — se atenuan. Son los informativos y, entre las percepciones, las
  // de especie: la columna se ve con su importe pero no suma.
  const INFO_COLS = [
    ...percs.map(c => !!c.noSuma || !!c.especie), false,
    ...dedus.map(c => !!c.noSuma), false,
    false,
    ...oblis.map(c => !!c.noSuma), false,
  ]

  return { percs, dedus, oblis, IMP_COLS, INFO_COLS }
}

/**
 * Fila de un empleado. Compartida por los dos agrupamientos para que los importes de
 * un empleado sean identicos en las dos versiones del reporte.
 */
const crearFilaDeLinea = ({ percs, dedus, oblis }) => (l) => {
  const percVals = percs.map(c => getVal(l, c.id))
  const deduVals = dedus.map(c => getVal(l, c.id))
  const obliVals = oblis.map(c => getVal(l, c.id))
  // Las percepciones en especie salen del total igual que los informativos: no se le
  // entregan en dinero al empleado, asi que no pueden llegar al NETO ni a la dispersion.
  const totalP = percVals.reduce((s, v, i) => s + (percs[i].noSuma || percs[i].especie ? 0 : v), 0)
  const totalD = deduVals.reduce((s, v, i) => s + (dedus[i].noSuma ? 0 : v), 0)
  const totalO = obliVals.reduce((s, v, i) => s + (oblis[i].noSuma ? 0 : v), 0)
  return {
    codigo: (l.codigo === null || l.codigo === undefined || l.codigo === '')
      ? '' : String(l.codigo).padStart(4, '0'),
    nombre: `${l.apellidoPaterno || ''} ${l.apellidoMaterno || ''} ${l.nombre || ''}`.trim(),
    // Solo se pinta en el modo por proyecto y en las hojas de Bajas/Vacaciones,
    // donde el departamento ya no es banner de seccion y si no se perderia de vista.
    departamento: l.departamentoNombre || 'SIN DEPARTAMENTO',
    departamentoNumero: l.departamentoNumero == null ? 0 : l.departamentoNumero,
    linea: l,
    numVals: [
      ...percVals, totalP, ...deduVals, totalD, totalP - totalD, ...obliVals, totalO,
    ],
  }
}

/**
 * Division de empleados: Registro Patronal IMSS → Departamento, como divide la lista
 * de raya tabular. Deja calculados `dep.filas`, `dep.totales`, `rp.totales` y el
 * total general, que consumen las dos hojas.
 */
function agruparPorRegistroPatronal(lineas, catalogo) {
  const { IMP_COLS } = catalogo
  const filaDeLinea = crearFilaDeLinea(catalogo)

  const regPats = new Map()
  ;(lineas || []).forEach(l => {
    const clave = l.registroPatronalIMSS || ''
    if (!regPats.has(clave)) {
      regPats.set(clave, {
        clave,
        numero: l.registroPatronalNumero == null ? Number.MAX_SAFE_INTEGER : l.registroPatronalNumero,
        deptos: new Map(),
      })
    }
    const rp   = regPats.get(clave)
    const dNom = l.departamentoNombre || 'SIN DEPARTAMENTO'
    const dNum = l.departamentoNumero == null ? 0 : l.departamentoNumero
    const dKey = `${dNum}|${dNom}`
    if (!rp.deptos.has(dKey)) rp.deptos.set(dKey, { numero: dNum, nombre: dNom, lineas: [] })
    rp.deptos.get(dKey).lineas.push(l)
  })

  const regPatsOrd = Array.from(regPats.values())
    .sort((a, b) => a.numero - b.numero || a.clave.localeCompare(b.clave))

  const totalGeneral = new Array(IMP_COLS).fill(0)
  let empleadosTotal = 0

  regPatsOrd.forEach(rp => {
    rp.totales   = new Array(IMP_COLS).fill(0)
    rp.empleados = 0
    rp.deptosOrd = Array.from(rp.deptos.values())
      .sort((a, b) => a.numero - b.numero || a.nombre.localeCompare(b.nombre))

    rp.deptosOrd.forEach(dep => {
      dep.totales = new Array(IMP_COLS).fill(0)
      dep.filas = dep.lineas.slice().sort(porCodigo).map(l => {
        const fila = filaDeLinea(l)
        sumar(dep.totales, fila.numVals)
        return fila
      })
      dep.empleados = dep.filas.length
      sumar(rp.totales, dep.totales)
      rp.empleados += dep.empleados
    })

    sumar(totalGeneral, rp.totales)
    empleadosTotal += rp.empleados
  })

  // Clave que se imprime en el encabezado: la unica que haya, o "Todos"
  const clavesRegPat = regPatsOrd.map(r => r.clave).filter(Boolean)
  const regPatHdr = clavesRegPat.length === 1
    ? clavesRegPat[0]
    : (clavesRegPat.length > 1 ? 'Todos' : '')

  return {
    regPatsOrd,
    totalGeneral,
    empleadosTotal,
    regPatHdr,
    variosRegPat: regPatsOrd.length > 1,
  }
}

/**
 * Division por PROYECTO. El proyecto lo resuelve el backend: sale del departamento
 * del empleado (Departamento.proyectoId) y, si ese no tiene, del proyecto general de
 * Configuracion de Nomina. Aqui solo se agrupa.
 *
 * Proyecto no tiene clave ni numero, solo nombre, asi que el orden es alfabetico y
 * "SIN PROYECTO" se manda al final.
 */
const SIN_PROY = 'SIN PROYECTO'
function agruparPorProyecto(lineas, catalogo) {
  const { IMP_COLS } = catalogo
  const filaDeLinea = crearFilaDeLinea(catalogo)

  const proyectos = new Map()
  ;(lineas || []).forEach(l => {
    const nombre = (l.proyectoNombre || '').trim() || SIN_PROY
    if (!proyectos.has(nombre)) proyectos.set(nombre, { nombre, lineas: [] })
    proyectos.get(nombre).lineas.push(l)
  })
  const proyectosOrd = Array.from(proyectos.values()).sort((a, b) => {
    if (a.nombre === SIN_PROY) return 1
    if (b.nombre === SIN_PROY) return -1
    return a.nombre.localeCompare(b.nombre)
  })

  proyectosOrd.forEach(proy => {
    proy.totales = new Array(IMP_COLS).fill(0)
    proy.filas = proy.lineas
      .slice()
      // Dentro del proyecto se ordena por departamento y luego por codigo, para que
      // los companeros de un mismo departamento salgan juntos.
      .sort((a, b) => {
        const da = a.departamentoNumero == null ? 0 : a.departamentoNumero
        const db = b.departamentoNumero == null ? 0 : b.departamentoNumero
        if (da !== db) return da - db
        const na = (a.departamentoNombre || '').localeCompare(b.departamentoNombre || '')
        if (na !== 0) return na
        return porCodigo(a, b)
      })
      .map(l => {
        const fila = filaDeLinea(l)
        sumar(proy.totales, fila.numVals)
        return fila
      })
    proy.empleados = proy.filas.length
  })

  return proyectosOrd
}

/**
 * Fusiona las lineas de TODAS las prenominas en UNA por empleado, sumando el importe
 * de cada concepto.
 *
 * El resultado tiene la misma forma que una linea del backend, asi que la hoja se
 * arma despues con `agruparPorRegistroPatronal` + `hojaListaRaya` sin tocarlas: sale
 * una lista de raya normal, pero con la suma de los periodos seleccionados.
 *
 * Los datos de identidad (nombre, codigo, departamento, registro patronal) se toman
 * de la ULTIMA prenomina en la que aparece el empleado — `prenominas` viene ordenada
 * por fecha de pago, asi que es su situacion mas reciente. Quien cambio de
 * departamento a media seleccion queda en el que tiene hoy, que es como se lee un
 * concentrado.
 *
 * Los importes se normalizan a `importeTotal` con `importeTotalManual` en null: el
 * acumulado ya trae aplicado el valor manual de cada linea, y dejar el campo con
 * valor haria que `efectivo()` leyera el de un solo periodo.
 */
function fusionarLineasPorEmpleado(lista) {
  const porEmpleado = new Map()
  let sinEmpleado = 0

  ;(lista || []).forEach(prenomina => {
    ;(prenomina.lineas || []).forEach(l => {
      // Sin empleadoId no hay con quien fusionar: se le da una clave propia para no
      // perder el renglon ni mezclarlo con el de otro.
      const clave = l.empleadoId ? l.empleadoId.toString() : `sin-empleado-${sinEmpleado++}`
      const previo = porEmpleado.get(clave)

      if (!previo) {
        porEmpleado.set(clave, {
          ...l,
          conceptos: (l.conceptos || []).map(c => ({
            ...c, importeTotal: efectivo(c), importeTotalManual: null,
          })),
        })
        return
      }

      const conceptos = new Map(previo.conceptos.map(c => [String(c.conceptoNominaId), c]))
      ;(l.conceptos || []).forEach(c => {
        const id = String(c.conceptoNominaId)
        const acumulado = conceptos.get(id)
        if (acumulado) acumulado.importeTotal += efectivo(c)
        else conceptos.set(id, { ...c, importeTotal: efectivo(c), importeTotalManual: null })
      })

      porEmpleado.set(clave, {
        ...l,
        conceptos: Array.from(conceptos.values()),
        totalPercepciones: (previo.totalPercepciones || 0) + (l.totalPercepciones || 0),
        totalDeducciones:  (previo.totalDeducciones  || 0) + (l.totalDeducciones  || 0),
        totalObligaciones: (previo.totalObligaciones || 0) + (l.totalObligaciones || 0),
        totalNeto:         (previo.totalNeto         || 0) + (l.totalNeto         || 0),
        esBaja:          !!previo.esBaja          || !!l.esBaja,
        tieneVacaciones: !!previo.tieneVacaciones || !!l.tieneVacaciones,
      })
    })
  })

  return Array.from(porEmpleado.values())
}

// ─── Textos del encabezado ────────────────────────────────────────────────────
const textoPeriodo = (prenomina) => {
  const fi = prenomina.fechaInicio ? dayjs(prenomina.fechaInicio).format('DD/MM/YYYY') : ''
  const ff = prenomina.fechaFin    ? dayjs(prenomina.fechaFin).format('DD/MM/YYYY')    : ''
  const pn = prenomina.periodoNumero
  return [
    pn != null ? `Periodo ${pn}` : 'Periodo',
    prenomina.tipoPeriodoNombre || '',
    fi && ff ? `· del ${fi} al ${ff}` : '',
  ].filter(Boolean).join(' ')
}

/**
 * Encabezado de las hojas globales cuando hay varias prenominas: en vez del periodo
 * de una, el rango completo que abarca la seleccion.
 */
const textoPeriodoRango = (prenominas) => {
  if (prenominas.length === 1) return textoPeriodo(prenominas[0])
  const inicios = prenominas.map(p => p.fechaInicio).filter(Boolean).sort()
  const fines   = prenominas.map(p => p.fechaFin).filter(Boolean).sort()
  const fi = inicios.length ? dayjs(inicios[0]).format('DD/MM/YYYY') : ''
  const ff = fines.length ? dayjs(fines[fines.length - 1]).format('DD/MM/YYYY') : ''
  return [
    `${prenominas.length} periodos`,
    fi && ff ? `· del ${fi} al ${ff}` : '',
  ].filter(Boolean).join(' ')
}

const textoMetaFiscal = (regPatHdr, empresaRfc) => [
  regPatHdr ? `Reg. Pat. IMSS ${regPatHdr}` : '',
  empresaRfc ? `RFC ${empresaRfc}` : '',
].filter(Boolean).join('  ·  ')

// ─── Hojas ────────────────────────────────────────────────────────────────────

/** Encabezados de las columnas de importe: los mismos en todas las hojas. */
const encabezadosImporte = ({ percs, dedus, oblis }) => {
  const rotulo = c => c.noSuma ? `${c.descripcion} (informativo)` : c.descripcion
  return [
    ...percs.map(c => cs(rotulo(c), hdr())),
    cs('TOTAL PERCEPCIONES', hdr(TEAL_OSC)),
    ...dedus.map(c => cs(rotulo(c), hdr())),
    cs('TOTAL DEDUCCIONES',  hdr(TEAL_OSC)),
    cs('NETO',               hdr(TEAL_OSC)),
    ...oblis.map(c => cs(rotulo(c), hdr())),
    cs('TOTAL OBLIGACIONES', hdr(TEAL_OSC)),
  ]
}

/**
 * Columnas opcionales de identidad del empleado (`opciones.datosEmpleado`), que entran
 * justo despues de "Empleado". Apagadas por omision: son datos personales y el libro
 * circula por correo, asi que el archivo de siempre no cambia de forma a menos que se pidan.
 *
 * Los tres son IDENTIFICADORES, no numeros: van por `cs` (que ya fija `t: 's'`) mas un
 * estilo con `numFmt: '@'`. El que de verdad se rompe sin eso es el NSS — puro digito, y
 * como numero pierde el cero inicial y se alinea a la derecha. Misma trampa que ya obligo a
 * tratar como texto la cuenta y la CLABE en la dispersion bancaria.
 */
const COLS_DATOS_EMPLEADO = [
  { titulo: 'RFC',  wch: 15, valor: (f) => f.linea?.rfc || '' },
  { titulo: 'NSS',  wch: 13, valor: (f) => f.linea?.numSeguridadSocial || '' },
  { titulo: 'CURP', wch: 20, valor: (f) => f.linea?.curp || '' },
]

/**
 * Hoja de detalle: Registro Patronal → Departamento → empleados.
 *
 * @param {Array} columnasExtra  `[{ titulo, wch, valor: (fila) => string }]` — columnas de
 *   TEXTO que se insertan despues de "Empleado". Mismo contrato que `hojaPlana`; `valor`
 *   recibe la fila completa, asi que puede leer la linea cruda en `f.linea`.
 * @param {Object} [incidencias]  bloque de dias al final del renglon:
 *   `{ dias, indice, observaciones }` — ver `incidenciasListaRaya.js`. Va DESPUES de los
 *   importes, no entre las columnas de texto, para no mover `FIJAS` ni los merges de los totales.
 */
function hojaListaRaya({ catalogo, grupos, encabezado, subtitulo, columnasExtra = [], incidencias = null }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const { regPatsOrd, totalGeneral, variosRegPat } = grupos
  // El bloque de columnas de texto se cuenta UNA vez y de aqui sale todo lo demas: el ancho
  // del banner, el relleno de los renglones de total y su merge. Antes el 2 estaba disperso
  // —incluido un `c: 1` a mano en los dos merges—, que es justo lo que permitia desfasarlos.
  const FIJAS    = 2 + columnasExtra.length  // Codigo | Empleado | ...extras
  // El bloque de incidencias cuelga del final: N dias + TOTAL + (OBSERVACIONES). Cuenta para el
  // ancho de la hoja (los banners y el merge del renglon de total llegan hasta ULT) pero NO para
  // ULT_FIJA, que sigue siendo la ultima columna de texto de la IZQUIERDA.
  // Sin dias no hay rejilla que pintar: una prenomina sin fechas de periodo dejaria un TOTAL y
  // unas OBSERVACIONES colgando de la nada.
  const dias     = incidencias?.dias || []
  const bloque   = dias.length ? incidencias : null
  const conObs   = !!bloque?.observaciones
  const N_INC    = bloque ? dias.length + 1 + (conObs ? 1 : 0) : 0
  const N_COLS   = FIJAS + IMP_COLS + N_INC
  const filas = [], merges = []
  const ULT      = N_COLS - 1                // ultima columna de la hoja
  const ULT_FIJA = FIJAS - 1                 // ultima columna de texto (etiqueta de los totales)
  const leyenda  = bloque
    ? 'Incidencias: el número son faltas (es lo que suma el TOTAL) · las claves —VAC, INC, RET…— son las del catálogo de tipos de incidencia'
    : null
  encabezado(filas, merges, subtitulo, ULT, leyenda)

  // Se calcula UNA vez por empleado y se reusa en el renglon y en los totales: `rejillaDeLinea`
  // recorre las incidencias y no conviene repetirlo por cada celda.
  const rejillas = new Map()
  const rejillaDe = (f) => {
    if (!bloque) return null
    if (!rejillas.has(f)) rejillas.set(f, rejillaDeLinea(f.linea, dias, bloque.indice))
    return rejillas.get(f)
  }
  // Suma por columna de dia + el total de faltas del grupo. Solo suma los marcadores NUMERICOS,
  // que son las faltas: es lo mismo que hace el =SUM() de Excel, que ignora el texto de las
  // celdas de vacaciones o incapacidad.
  const totalesIncidencias = (filasDelGrupo) => {
    const porDia = dias.map(() => 0)
    let faltas = 0
    filasDelGrupo.forEach((f) => {
      const r = rejillaDe(f)
      if (!r) return
      faltas += r.totalFaltas
      r.celdas.forEach((celda, k) => {
        if (celda && typeof celda.valor === 'number') porDia[k] += celda.valor
      })
    })
    return { porDia, faltas }
  }
  const celdasIncidencias = (f, impar) => {
    if (!bloque) return []
    const { celdas, totalFaltas } = rejillaDe(f)
    return [
      ...celdas.map((celda) => {
        if (!celda) return ce(sDia(impar, null))
        return typeof celda.valor === 'number'
          ? cn(celda.valor, sDiaNum(impar))
          : cs(celda.valor, sDia(impar, celda.cubeta))
      }),
      cn(totalFaltas, sEnteros(impar)),
      ...(conObs ? [ce(sObs(impar))] : []),
    ]
  }
  const celdasTotalIncidencias = (filasDelGrupo, estiloNum, estiloRel) => {
    if (!bloque) return []
    const { porDia, faltas } = totalesIncidencias(filasDelGrupo)
    return [
      ...porDia.map((v) => cn(v, estiloNum)),
      cn(faltas, estiloNum),
      ...(conObs ? [ce(estiloRel)] : []),
    ]
  }

  filas.push([
    cs('Código', hdrTxt()),
    cs('Empleado', hdrTxt()),
    ...columnasExtra.map(c => cs(c.titulo, hdrTxt())),
    ...encabezadosImporte(catalogo),
    // El dia del mes y el dia de la semana caben en la misma celda en dos renglones: asi el
    // bloque de encabezado sigue midiendo 10 filas y los encabezados quedan en la decima.
    ...(bloque ? dias.map(d => cs(`${d.diaMes}\n${d.etiqueta}`, hdrDia(d.esSeptimo))) : []),
    ...(bloque ? [cs('TOTAL FALTAS', hdr(TEAL_OSC))] : []),
    ...(conObs ? [cs('OBSERVACIONES', hdrTxt())] : []),
  ])

  regPatsOrd.forEach(rp => {
    // El banner de registro patronal solo aporta cuando hay mas de uno
    if (variosRegPat) {
      filas.push([])
      filas.push([cs(`Reg. Pat. IMSS ${rp.clave || '— sin registro patronal'}`, sRegPat)])
    }

    rp.deptosOrd.forEach(dep => {
      // Los banners y los totales van fusionados: como las celdas de al lado llevan
      // relleno, sin merge Excel cortaria el texto al ancho de su columna.
      const fBanner = filas.length
      filas.push([
        cs(`${dep.numero}  ${dep.nombre.toUpperCase()}`, sDepto),
        ...Array.from({ length: N_COLS - 1 }, () => ce(sDeptoRel)),
      ])
      merges.push({ s: { r: fBanner, c: 0 }, e: { r: fBanner, c: ULT } })

      dep.filas.forEach((f, i) => {
        const impar = i % 2 === 1
        filas.push([
          cs(f.codigo, sCod(impar)),
          cs(f.nombre, sNom(impar)),
          // sCod y no sNom: es el estilo de texto que trae numFmt '@'. Sin el la celda queda
          // en formato General y Excel puede reconvertir el NSS a numero al re-teclearla.
          ...columnasExtra.map(c => cs(c.valor(f), sCod(impar))),
          ...f.numVals.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
          ...celdasIncidencias(f, impar),
        ])
      })

      const fTotal = filas.length
      filas.push([
        cs(`Total ${dep.nombre.toUpperCase()}`, sTotLbl),
        ...Array.from({ length: ULT_FIJA }, () => ce(sTotRel)),
        ...dep.totales.map((v, k) => cn(v, sTotNum(v, INFO_COLS[k]))),
        ...celdasTotalIncidencias(dep.filas, sTotEnt, sTotRel),
      ])
      merges.push({ s: { r: fTotal, c: 0 }, e: { r: fTotal, c: ULT_FIJA } })
      filas.push([])
    })
  })

  const todasLasFilas = regPatsOrd.flatMap(rp => rp.deptosOrd.flatMap(dep => dep.filas))
  const fGral = filas.length
  filas.push([
    cs('TOTAL GENERAL', sGTLbl),
    ...Array.from({ length: ULT_FIJA }, () => ce(sGTRel)),
    ...totalGeneral.map((v, k) => cn(v, sGTNum(v, INFO_COLS[k]))),
    ...celdasTotalIncidencias(todasLasFilas, sGTEnt, sGTRel),
  ])
  merges.push({ s: { r: fGral, c: 0 }, e: { r: fGral, c: ULT_FIJA } })

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  // La fila de aire (la novena) mide 7pt y no alcanza para la leyenda del bloque de incidencias.
  // `!rows` se asigna por hoja, asi que subirla aqui no toca a las demas.
  ws['!rows']   = leyenda
    ? [...ALTOS_ENCABEZADO.slice(0, 8), { hpt: 14 }, ALTOS_ENCABEZADO[9]]
    : ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 11 },  // Código
    { wch: 34 },  // Empleado
    ...columnasExtra.map(c => ({ wch: c.wch || 18 })),
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
    ...(bloque ? dias.map(() => ({ wch: 6 })) : []),
    ...(bloque ? [{ wch: 12 }] : []),
    ...(conObs ? [{ wch: 30 }] : []),
  ]
  return ws
}

/**
 * Concentrado por departamento: un renglon por departamento con el total de cada
 * concepto, mas el total general al cierre. Comparte las columnas de IMPORTE con la hoja de
 * detalle para poder leerse igual; las de texto no siempre coinciden — con
 * `opciones.datosEmpleado` el detalle lleva tres columnas mas y los importes se recorren.
 */
function hojaConcentrado({ catalogo, grupos, encabezado, subtitulo }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const { regPatsOrd, totalGeneral, empleadosTotal, variosRegPat } = grupos
  const filas = [], merges = []
  const N   = 2 + IMP_COLS   // Departamento + Empleados + importes
  const ULT = N - 1
  encabezado(filas, merges, subtitulo, ULT)

  filas.push([cs('Departamento', hdrTxt()), cs('Empleados', hdr()), ...encabezadosImporte(catalogo)])

  regPatsOrd.forEach(rp => {
    let i = 0
    if (variosRegPat) {
      const fBanner = filas.length
      filas.push([
        cs(`Reg. Pat. IMSS ${rp.clave || '— sin registro patronal'}`, sDepto),
        ...Array.from({ length: N - 1 }, () => ce(sDeptoRel)),
      ])
      merges.push({ s: { r: fBanner, c: 0 }, e: { r: fBanner, c: ULT } })
    }

    rp.deptosOrd.forEach(dep => {
      const impar = i++ % 2 === 1
      filas.push([
        cs(`${dep.numero}  ${dep.nombre.toUpperCase()}`, sNomDepto(impar)),
        cn(dep.empleados, sEnteros(impar)),
        ...dep.totales.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
      ])
    })

    if (variosRegPat) {
      filas.push([
        cs(`Subtotal Reg. Pat. ${rp.clave || '— sin registro patronal'}`, sTotLbl),
        cn(rp.empleados, sTotEnt),
        ...rp.totales.map((v, k) => cn(v, sTotNum(v, INFO_COLS[k]))),
      ])
    }
  })

  filas.push([
    cs('TOTAL GENERAL', sGTLbl),
    cn(empleadosTotal, sGTEnt),
    ...totalGeneral.map((v, k) => cn(v, sGTNum(v, INFO_COLS[k]))),
  ])

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  ws['!rows']   = ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 34 },  // Departamento
    { wch: 11 },  // Empleados
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
  ]
  return ws
}

/**
 * Concentrado global cuando hay varias prenominas: un renglon por prenomina con el
 * total de cada concepto, y debajo el desglose por departamento. Es el equivalente
 * multi-periodo del Concentrado de siempre.
 */
function hojaConcentradoPrenominas({ catalogo, porPrenomina, encabezado }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const filas = [], merges = []
  const N   = 2 + IMP_COLS   // Periodo/Departamento + Empleados + importes
  const ULT = N - 1
  encabezado(filas, merges, 'Concentrado de conceptos por periodo', ULT)

  filas.push([cs('Periodo / Departamento', hdrTxt()), cs('Empleados', hdr()), ...encabezadosImporte(catalogo)])

  const totalGeneral = new Array(IMP_COLS).fill(0)
  let empleadosTotal = 0

  porPrenomina.forEach(({ prenomina, grupos }) => {
    const fBanner = filas.length
    filas.push([
      cs((prenomina.nombre || textoPeriodo(prenomina)).toUpperCase(), sDepto),
      ...Array.from({ length: N - 1 }, () => ce(sDeptoRel)),
    ])
    merges.push({ s: { r: fBanner, c: 0 }, e: { r: fBanner, c: ULT } })

    // Desglose por departamento dentro del periodo, sumando los registros patronales
    const porDepto = new Map()
    grupos.regPatsOrd.forEach(rp => {
      rp.deptosOrd.forEach(dep => {
        const clave = `${dep.numero}|${dep.nombre}`
        if (!porDepto.has(clave)) {
          porDepto.set(clave, {
            nombre: dep.nombre, numero: dep.numero,
            empleados: 0, totales: new Array(IMP_COLS).fill(0),
          })
        }
        const d = porDepto.get(clave)
        d.empleados += dep.empleados
        sumar(d.totales, dep.totales)
      })
    })

    Array.from(porDepto.values())
      .sort((a, b) => a.numero - b.numero || a.nombre.localeCompare(b.nombre))
      .forEach((d, i) => {
        const impar = i % 2 === 1
        filas.push([
          cs(`   ${d.nombre.toUpperCase()}`, sNomDepto(impar)),
          cn(d.empleados, sEnteros(impar)),
          ...d.totales.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
        ])
      })

    filas.push([
      cs(`Subtotal ${(prenomina.nombre || '').toUpperCase()}`, sTotLbl),
      cn(grupos.empleadosTotal, sTotEnt),
      ...grupos.totalGeneral.map((v, k) => cn(v, sTotNum(v, INFO_COLS[k]))),
    ])

    sumar(totalGeneral, grupos.totalGeneral)
    empleadosTotal += grupos.empleadosTotal
  })

  filas.push([
    cs('TOTAL GENERAL', sGTLbl),
    cn(empleadosTotal, sGTEnt),
    ...totalGeneral.map((v, k) => cn(v, sGTNum(v, INFO_COLS[k]))),
  ])

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  ws['!rows']   = ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 34 },  // Periodo / Departamento
    { wch: 11 },  // Empleados
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
  ]
  return ws
}

/**
 * Hoja plana filtrada (Bajas / Vacaciones): un renglon por empleado con el periodo y
 * el departamento como columnas, porque aqui ya no hay banners de seccion. Replica
 * las hojas BAJAS y VACACIONES del formato del cliente.
 *
 * @param {Array} filasConPeriodo [{ prenomina, fila }]
 * @param {Array} columnasExtra   [{ titulo, wch, valor: (fila) => string }]
 */
function hojaPlana({ catalogo, filasConPeriodo, encabezado, subtitulo, columnasExtra = [] }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const filas = [], merges = []
  const N   = 3 + columnasExtra.length + IMP_COLS
  const ULT = N - 1
  encabezado(filas, merges, subtitulo, ULT)

  filas.push([
    cs('Periodo', hdrTxt()),
    cs('Código', hdrTxt()),
    cs('Empleado', hdrTxt()),
    ...columnasExtra.map(c => cs(c.titulo, hdrTxt())),
    ...encabezadosImporte(catalogo),
  ])

  const totales = new Array(IMP_COLS).fill(0)

  filasConPeriodo.forEach(({ prenomina, fila }, i) => {
    const impar = i % 2 === 1
    filas.push([
      cs(prenomina.nombre || '', sNom(impar)),
      cs(fila.codigo, sCod(impar)),
      cs(fila.nombre, sNom(impar)),
      ...columnasExtra.map(c => cs(c.valor(fila), sFecha(impar))),
      ...fila.numVals.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
    ])
    sumar(totales, fila.numVals)
  })

  if (filasConPeriodo.length === 0) {
    filas.push([cs('No hay empleados que cumplan el criterio en los periodos seleccionados', sNom(false))])
  }

  const fTotal = filas.length
  filas.push([
    cs('TOTAL GENERAL', sGTLbl),
    ...Array.from({ length: 2 + columnasExtra.length }, () => ce(sGTRel)),
    ...totales.map((v, k) => cn(v, sGTNum(v, INFO_COLS[k]))),
  ])
  merges.push({ s: { r: fTotal, c: 0 }, e: { r: fTotal, c: 2 + columnasExtra.length } })

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  ws['!rows']   = ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 22 },  // Periodo
    { wch: 11 },  // Código
    { wch: 34 },  // Empleado
    ...columnasExtra.map(c => ({ wch: c.wch || 18 })),
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
  ]
  return ws
}

/** Hoja por proyecto: la lista va plana, con el departamento como columna. */
function hojaProyecto({ catalogo, proy, encabezado }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const N_COLS = 3 + IMP_COLS
  const filas = [], merges = []
  const ULT = N_COLS - 1
  encabezado(filas, merges, `Lista de raya · ${proy.nombre}`, ULT)

  filas.push([
    cs('Código', hdrTxt()),
    cs('Empleado', hdrTxt()),
    cs('Departamento', hdrTxt()),
    ...encabezadosImporte(catalogo),
  ])

  proy.filas.forEach((f, i) => {
    const impar = i % 2 === 1
    filas.push([
      cs(f.codigo, sCod(impar)),
      cs(f.nombre, sNom(impar)),
      cs(f.departamento, sNom(impar)),
      ...f.numVals.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
    ])
  })

  // El total va fusionado sobre las tres columnas de texto: las de al lado llevan
  // relleno y sin merge Excel cortaria la etiqueta.
  const fTotal = filas.length
  filas.push([
    cs(`Total ${proy.nombre.toUpperCase()}`, sTotLbl),
    ce(sTotRel),
    ce(sTotRel),
    ...proy.totales.map((v, k) => cn(v, sTotNum(v, INFO_COLS[k]))),
  ])
  merges.push({ s: { r: fTotal, c: 0 }, e: { r: fTotal, c: 2 } })

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  ws['!rows']   = ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 11 },  // Código
    { wch: 34 },  // Empleado
    { wch: 24 },  // Departamento
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
  ]
  return ws
}

/**
 * Concentrado por proyecto: un renglon por proyecto con su total, y debajo el
 * desglose por departamento dentro de ese proyecto, para poder amarrar cada hoja con
 * el total general.
 */
function hojaConcentradoProyectos({ catalogo, proyectosOrd, grupos, encabezado }) {
  const { IMP_COLS, INFO_COLS } = catalogo
  const { totalGeneral, empleadosTotal } = grupos
  const filas = [], merges = []
  const N   = 2 + IMP_COLS   // Proyecto/Departamento + Empleados + importes
  const ULT = N - 1
  encabezado(filas, merges, 'Concentrado de conceptos por proyecto', ULT)

  filas.push([cs('Proyecto / Departamento', hdrTxt()), cs('Empleados', hdr()), ...encabezadosImporte(catalogo)])

  proyectosOrd.forEach(proy => {
    const fBanner = filas.length
    filas.push([
      cs(proy.nombre.toUpperCase(), sDepto),
      ...Array.from({ length: N - 1 }, () => ce(sDeptoRel)),
    ])
    merges.push({ s: { r: fBanner, c: 0 }, e: { r: fBanner, c: ULT } })

    // Desglose por departamento dentro del proyecto
    const porDepto = new Map()
    proy.filas.forEach(f => {
      const clave = `${f.departamentoNumero}|${f.departamento}`
      if (!porDepto.has(clave)) {
        porDepto.set(clave, {
          nombre: f.departamento, numero: f.departamentoNumero,
          empleados: 0, totales: new Array(IMP_COLS).fill(0),
        })
      }
      const d = porDepto.get(clave)
      d.empleados += 1
      sumar(d.totales, f.numVals)
    })

    Array.from(porDepto.values())
      .sort((a, b) => a.numero - b.numero || a.nombre.localeCompare(b.nombre))
      .forEach((d, i) => {
        const impar = i % 2 === 1
        filas.push([
          cs(`   ${d.nombre.toUpperCase()}`, sNomDepto(impar)),
          cn(d.empleados, sEnteros(impar)),
          ...d.totales.map((v, k) => cn(v, sImp(v, INFO_COLS[k], impar))),
        ])
      })

    filas.push([
      cs(`Subtotal ${proy.nombre.toUpperCase()}`, sTotLbl),
      cn(proy.empleados, sTotEnt),
      ...proy.totales.map((v, k) => cn(v, sTotNum(v, INFO_COLS[k]))),
    ])
  })

  filas.push([
    cs('TOTAL GENERAL', sGTLbl),
    cn(empleadosTotal, sGTEnt),
    ...totalGeneral.map((v, k) => cn(v, sGTNum(v, INFO_COLS[k]))),
  ])

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!merges'] = merges
  ws['!rows']   = ALTOS_ENCABEZADO
  ws['!cols']   = [
    { wch: 34 },  // Proyecto / Departamento
    { wch: 11 },  // Empleados
    ...Array.from({ length: IMP_COLS }, () => ({ wch: 15 })),
  ]
  return ws
}

/**
 * Excel topa el nombre de hoja en 31 caracteres y prohibe : \ / ? * [ ]. Ademas no
 * admite nombres repetidos, asi que se deduplica con sufijo — con dos periodos de
 * nombre largo y prefijo comun el recorte los vuelve iguales.
 */
const crearNombreHoja = () => {
  const usados = new Set()
  return (texto) => {
    let base = (texto || 'Hoja').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Hoja'
    if (!usados.has(base)) { usados.add(base); return base }
    for (let i = 2; i < 1000; i++) {
      const sufijo = ` (${i})`
      const cand = `${base.slice(0, 31 - sufijo.length)}${sufijo}`
      if (!usados.has(cand)) { usados.add(cand); return cand }
    }
    return base
  }
}

// ─── Libro ────────────────────────────────────────────────────────────────────

/**
 * Arma el workbook completo. Funcion pura: no toca el DOM ni la red, para poder
 * verificarla con node.
 *
 * @param {Array}   prenominas  [{ _id, nombre, fechaInicio, fechaFin, periodoNumero,
 *                                 tipoPeriodoNombre, empresaNombre, empresaRfc, lineas }]
 * @param {Set}     noSumaIds   ids de conceptos informativos
 * @param {Set}     especieIds  ids de percepciones en especie (se ven, no suman al neto)
 * @param {Boolean} conLogo     si las filas 1-2 se dejan libres para la imagen
 * @param {Object}  opciones    { porProyecto, hojasPorPrenomina, concentrado,
 *                                concentradoEmpleados, bajas, vacaciones, datosEmpleado,
 *                                incidencias, filtroFiniquito: 'todos'|'sin'|'solo' }
 * @returns {{ wb: Object, nombreArchivo: String }}
 */
export function construirLibroListaRaya({ prenominas, noSumaIds, especieIds, conLogo = false, opciones = {} }) {
  const {
    porProyecto = false,
    hojasPorPrenomina = true,
    concentrado = true,
    concentradoEmpleados = false,
    bajas = false,
    vacaciones = false,
    // RFC / NSS / CURP despues de "Empleado", solo en las hojas de detalle por empleado.
    datosEmpleado = false,
    // Columnas de texto propias del llamador, con el mismo contrato `{ titulo, wch, valor }`.
    // Van despues de las de `datosEmpleado` y, como ellas, solo en el detalle por empleado.
    columnasExtra = [],
    // Rejilla de dias del periodo al final del renglon: `{ indice, observaciones }`, donde
    // `indice` sale de `construirIndiceTipos` con el catalogo de TiposIncidencias.
    //
    // Los DIAS no se reciben: se derivan de cada prenomina, porque cada una tiene su periodo.
    // Por eso el bloque solo entra en las hojas de detalle POR PRENOMINA. Queda fuera de:
    //   · `porProyecto`, cuya hoja tiene las columnas fijas cableadas;
    //   · los concentrados, que no tienen renglon por empleado;
    //   · `concentradoEmpleados`, que fusiona periodos distintos — una rejilla de dias ahi no
    //     significaria nada, porque el dia 3 seria de una semana o de otra segun la linea.
    incidencias = null,
    // 'todos' = la prenomina completa · 'sin' = sin liquidaciones · 'solo' = solo liquidaciones.
    // Se acepta tambien el `sinFiniquitos` booleano de antes, por si queda algun llamador.
    filtroFiniquito = opciones.sinFiniquitos ? 'sin' : 'todos',
  } = opciones

  // Las lineas se filtran ANTES de armar el catalogo de conceptos: asi las columnas
  // que solo existen por el finiquito (indemnizacion, prima de antiguedad, ISR de
  // separacion...) desaparecen solas cuando se piden "sin finiquitos", en vez de
  // quedar como columnas en cero — y al reves, "solo finiquitos" se queda sin las
  // columnas que solo trae la nomina ordinaria. El marcador es
  // LineaPrenomina.finiquitoId, que solo se llena en la linea a la que se le aplico
  // un finiquito (Controllers/Nomina/finiquito.controllers.js).
  const pasaFiltro = (l) => (
    filtroFiniquito === 'sin'  ? !l.finiquitoId
      : filtroFiniquito === 'solo' ? !!l.finiquitoId
        : true
  )
  const filtrando = filtroFiniquito === 'sin' || filtroFiniquito === 'solo'
  const lista = (prenominas || [])
    .filter(Boolean)
    .map(p => (filtrando ? { ...p, lineas: (p.lineas || []).filter(pasaFiltro) } : p))

  if (lista.length === 0) throw new Error('No hay prenóminas para armar la Lista de Raya')
  if (filtrando && lista.every(p => (p.lineas || []).length === 0)) {
    throw new Error(filtroFiniquito === 'sin'
      ? 'Todas las líneas son finiquitos: sin ellos no queda nada que exportar'
      : 'No hay ninguna línea de finiquito que exportar')
  }

  // Sufijo del nombre de archivo, para poder distinguir las tres descargas
  const sufFiniquitos = (filtroFiniquito === 'sin' ? '_sin_finiquitos'
    : filtroFiniquito === 'solo' ? '_solo_finiquitos'
      : '') + (incidencias ? '_con_incidencias' : '')

  const empresaNombre = lista[0].empresaNombre || ''
  const empresaRfc    = lista[0].empresaRfc || ''

  // El catalogo se arma sobre TODAS las lineas para que todas las hojas compartan
  // columnas y el concentrado global pueda sumarlas.
  const todasLineas = lista.flatMap(p => p.lineas || [])
  const catalogo = construirCatalogoConceptos(todasLineas, noSumaIds, especieIds)

  // Solo entran en las hojas de detalle por empleado. Los concentrados por departamento,
  // periodo o proyecto no tienen renglon por empleado, asi que no aplican.
  const colsEmpleado = [...(datosEmpleado ? COLS_DATOS_EMPLEADO : []), ...columnasExtra]

  const porPrenomina = lista.map(prenomina => ({
    prenomina,
    grupos: agruparPorRegistroPatronal(prenomina.lineas || [], catalogo),
  }))

  const wb = XLSX.utils.book_new()
  const nombreHoja = crearNombreHoja()
  const unaSola = lista.length === 1

  // ── Modo por proyecto: solo aplica con una prenomina (es el boton de la pantalla)
  if (porProyecto) {
    const prenomina = lista[0]
    const grupos = porPrenomina[0].grupos
    const proyectosOrd = agruparPorProyecto(prenomina.lineas || [], catalogo)
    const encabezado = crearEncabezado({
      conLogo,
      empresaNombre,
      periodoTxt: textoPeriodo(prenomina),
      metaFiscal: textoMetaFiscal(grupos.regPatHdr, empresaRfc),
    })

    proyectosOrd.forEach(proy =>
      XLSX.utils.book_append_sheet(wb, hojaProyecto({ catalogo, proy, encabezado }), nombreHoja(proy.nombre))
    )
    XLSX.utils.book_append_sheet(
      wb,
      hojaConcentradoProyectos({ catalogo, proyectosOrd, grupos, encabezado }),
      nombreHoja('Concentrado')
    )

    return {
      wb,
      nombreArchivo: `lista_raya_por_proyecto${sufFiniquitos}_${limpiarNombre(prenomina.nombre)}.xlsx`,
    }
  }

  // ── Una hoja de detalle por prenomina
  if (hojasPorPrenomina) {
    porPrenomina.forEach(({ prenomina, grupos }) => {
      const encabezado = crearEncabezado({
        conLogo,
        empresaNombre,
        periodoTxt: textoPeriodo(prenomina),
        metaFiscal: textoMetaFiscal(grupos.regPatHdr, empresaRfc),
      })
      XLSX.utils.book_append_sheet(
        wb,
        hojaListaRaya({
          catalogo, grupos, encabezado,
          subtitulo: 'Lista de raya',
          columnasExtra: colsEmpleado,
          // Los dias son los de ESTA prenomina: el bloque se arma por hoja, no una vez.
          incidencias: incidencias
            ? { ...incidencias, dias: construirDiasPeriodo(prenomina) }
            : null,
        }),
        // Con una sola prenomina el nombre se queda como siempre, para no cambiarle
        // el archivo a quien ya lo usa.
        nombreHoja(unaSola ? 'Lista de Raya' : (prenomina.nombre || textoPeriodo(prenomina)))
      )
    })
  }

  // ── Concentrado: por departamento si es una, por periodo si son varias
  if (concentrado) {
    if (unaSola) {
      const { prenomina, grupos } = porPrenomina[0]
      const encabezado = crearEncabezado({
        conLogo,
        empresaNombre,
        periodoTxt: textoPeriodo(prenomina),
        metaFiscal: textoMetaFiscal(grupos.regPatHdr, empresaRfc),
      })
      XLSX.utils.book_append_sheet(
        wb,
        hojaConcentrado({
          catalogo, grupos, encabezado,
          subtitulo: 'Concentrado de conceptos por departamento',
        }),
        nombreHoja('Concentrado')
      )
    } else {
      const encabezado = crearEncabezado({
        conLogo,
        empresaNombre,
        periodoTxt: textoPeriodoRango(lista),
        metaFiscal: textoMetaFiscal('', empresaRfc),
      })
      XLSX.utils.book_append_sheet(
        wb,
        hojaConcentradoPrenominas({ catalogo, porPrenomina, encabezado }),
        nombreHoja('Concentrado')
      )
    }
  }

  // ── Concentrado por empleado: la lista de raya de siempre, pero con cada
  //    empleado una sola vez y sus importes sumados a lo largo de la seleccion.
  if (concentradoEmpleados) {
    const lineasFusionadas = fusionarLineasPorEmpleado(lista)
    const gruposFusion = agruparPorRegistroPatronal(lineasFusionadas, catalogo)
    const encabezado = crearEncabezado({
      conLogo,
      empresaNombre,
      periodoTxt: textoPeriodoRango(lista),
      metaFiscal: textoMetaFiscal(gruposFusion.regPatHdr, empresaRfc),
    })
    XLSX.utils.book_append_sheet(
      wb,
      hojaListaRaya({
        catalogo,
        grupos: gruposFusion,
        encabezado,
        subtitulo: unaSola
          ? 'Concentrado por empleado'
          : `Concentrado por empleado · suma de ${lista.length} periodos`,
        // fusionarLineasPorEmpleado arranca con { ...l }, asi que rfc/nss/curp sobreviven
        // la fusion con el valor de la ultima prenomina — misma regla que nombre y codigo.
        columnasExtra: colsEmpleado,
      }),
      nombreHoja('Concentrado Empleados')
    )
  }

  // ── Hojas opcionales del formato del cliente
  const encabezadoGlobal = crearEncabezado({
    conLogo,
    empresaNombre,
    periodoTxt: textoPeriodoRango(lista),
    metaFiscal: textoMetaFiscal('', empresaRfc),
  })
  const filaDeLinea = crearFilaDeLinea(catalogo)

  const aplanar = (predicado) => {
    const salida = []
    porPrenomina.forEach(({ prenomina }) => {
      ;(prenomina.lineas || [])
        .filter(predicado)
        .slice()
        .sort(porCodigo)
        .forEach(l => salida.push({ prenomina, fila: filaDeLinea(l) }))
    })
    return salida
  }

  if (bajas) {
    XLSX.utils.book_append_sheet(
      wb,
      hojaPlana({
        catalogo,
        // `esBaja` lo marca el backend: el empleado tiene fechaBaja dentro del rango
        // de las prenominas elegidas.
        filasConPeriodo: aplanar(l => !!l.esBaja),
        encabezado: encabezadoGlobal,
        subtitulo: 'Bajas del periodo',
        columnasExtra: [
          { titulo: 'Departamento', wch: 24, valor: f => f.departamento },
          { titulo: 'Fecha de baja', wch: 14, valor: f => f.linea.fechaBajaTexto || '' },
          { titulo: 'Motivo', wch: 30, valor: f => f.linea.motivoBaja || '' },
        ],
      }),
      nombreHoja('Bajas')
    )
  }

  if (vacaciones) {
    XLSX.utils.book_append_sheet(
      wb,
      hojaPlana({
        catalogo,
        // `tieneVacaciones` lo marca el backend: la linea trae importe distinto de
        // cero en algun concepto esVacaciones / esPrimaVacacional.
        filasConPeriodo: aplanar(l => !!l.tieneVacaciones),
        encabezado: encabezadoGlobal,
        subtitulo: 'Vacaciones y prima vacacional',
        columnasExtra: [
          { titulo: 'Departamento', wch: 24, valor: f => f.departamento },
        ],
      }),
      nombreHoja('Vacaciones')
    )
  }

  if (wb.SheetNames.length === 0) {
    throw new Error('No se seleccionó ninguna hoja para el reporte')
  }

  const nombreArchivo = unaSola
    ? `lista_raya${sufFiniquitos}_${limpiarNombre(lista[0].nombre)}.xlsx`
    : `lista_raya${sufFiniquitos}_${lista.length}_periodos.xlsx`

  return { wb, nombreArchivo }
}

const limpiarNombre = (nombre) => (nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')

// ─── Dispersion bancaria ──────────────────────────────────────────────────────

/**
 * Posicion de la columna NETO dentro de `numVals`. Se deriva del catalogo en vez de
 * escribirla a mano para que no se despegue si cambia el orden de las columnas.
 */
const indiceNeto = ({ percs, dedus }) => percs.length + 1 + dedus.length + 1

/**
 * Libro de dispersion: una hoja por banco pagador y una de efectivo.
 *
 * `Empleado.bancoPagoElectronicoId` apunta a la cuenta de la EMPRESA con la que se
 * le paga —por eso el archivo del cliente tiene una hoja "DISPERSION BBVA" y la
 * caratula habla aparte de "Dispersion BANORTE"—, asi que agrupar por ese campo da
 * justo el archivo que se sube a cada banco.
 *
 * Lo que decide si alguien va a dispersion o a efectivo es tener cuenta o CLABE
 * capturada, no su forma de pago: sin cuenta no hay nada que dispersar.
 *
 * El NETO se calcula con el mismo catalogo de conceptos que la Lista de Raya, no con
 * `totalNeto`, para que los dos reportes no puedan discrepar en lo que se paga.
 */
export function construirLibroDispersion({ prenomina, bancosPorId, noSumaIds, especieIds, conLogo = false }) {
  const lineas = prenomina?.lineas || []
  if (lineas.length === 0) throw new Error('La prenómina no tiene líneas')

  const catalogo = construirCatalogoConceptos(lineas, noSumaIds, especieIds)
  const filaDeLinea = crearFilaDeLinea(catalogo)
  const iNeto = indiceNeto(catalogo)
  const grupos = agruparPorRegistroPatronal(lineas, catalogo)

  const encabezado = crearEncabezado({
    conLogo,
    empresaNombre: prenomina.empresaNombre || '',
    periodoTxt: textoPeriodo(prenomina),
    metaFiscal: textoMetaFiscal(grupos.regPatHdr, prenomina.empresaRfc || ''),
  })

  const SIN_CUENTA = '__efectivo__'
  const porBanco = new Map()

  lineas.forEach((l) => {
    const cuenta = (l.numCuenta || '').trim()
    const clabe = (l.clabeInterbancaria || '').trim()
    const tieneCuenta = !!(cuenta || clabe)
    const bancoId = tieneCuenta && l.bancoPagoElectronicoId
      ? l.bancoPagoElectronicoId.toString()
      : SIN_CUENTA

    if (!porBanco.has(bancoId)) porBanco.set(bancoId, [])
    porBanco.get(bancoId).push({ fila: filaDeLinea(l), cuenta, clabe })
  })

  const nombreBanco = (id) => {
    if (id === SIN_CUENTA) return 'Efectivo'
    const banco = bancosPorId ? bancosPorId[id] : null
    if (!banco) return 'Sin banco'
    return banco.nombre || banco.banco || 'Sin banco'
  }

  // Efectivo al final; los bancos por nombre
  const ordenados = [...porBanco.keys()].sort((a, b) => {
    if (a === SIN_CUENTA) return 1
    if (b === SIN_CUENTA) return -1
    return nombreBanco(a).localeCompare(nombreBanco(b), 'es')
  })

  const wb = XLSX.utils.book_new()
  const nombreHoja = crearNombreHoja()

  ordenados.forEach((bancoId) => {
    const esEfectivo = bancoId === SIN_CUENTA
    const titulo = nombreBanco(bancoId)
    const registros = porBanco.get(bancoId).slice().sort((a, b) => porCodigo(a.fila, b.fila))

    const filas = [], merges = []
    // Código | Empleado | Cuenta | CLABE | Neto  (en efectivo no van cuenta ni CLABE)
    const ULT = esEfectivo ? 2 : 4
    encabezado(filas, merges, esEfectivo ? 'Pago en efectivo' : `Dispersión · ${titulo}`, ULT)

    filas.push(
      esEfectivo
        ? [cs('Código', hdrTxt()), cs('Empleado', hdrTxt()), cs('NETO', hdr(TEAL_OSC))]
        : [
            cs('Código', hdrTxt()), cs('Empleado', hdrTxt()),
            cs('Cuenta', hdrTxt()), cs('CLABE', hdrTxt()), cs('NETO', hdr(TEAL_OSC)),
          ]
    )

    let total = 0
    registros.forEach(({ fila, cuenta, clabe }, i) => {
      const impar = i % 2 === 1
      const neto = fila.numVals[iNeto]
      total += neto
      filas.push(
        esEfectivo
          ? [cs(fila.codigo, sCod(impar)), cs(fila.nombre, sNom(impar)), cn(neto, sImp(neto, false, impar))]
          : [
              cs(fila.codigo, sCod(impar)),
              cs(fila.nombre, sNom(impar)),
              // Cuenta y CLABE van como texto: son identificadores, y como numero
              // Excel les come los ceros de la izquierda y los pasa a notacion
              // cientifica en cuanto tienen 18 digitos.
              cs(cuenta, sCod(impar)),
              cs(clabe, sCod(impar)),
              cn(neto, sImp(neto, false, impar)),
            ]
      )
    })

    const fTotal = filas.length
    filas.push([
      cs(`TOTAL ${titulo.toUpperCase()} (${registros.length})`, sGTLbl),
      ...Array.from({ length: ULT - 1 }, () => ce(sGTRel)),
      cn(total, sGTNum(total, false)),
    ])
    merges.push({ s: { r: fTotal, c: 0 }, e: { r: fTotal, c: ULT - 1 } })

    const ws = XLSX.utils.aoa_to_sheet(filas)
    ws['!merges'] = merges
    ws['!rows'] = ALTOS_ENCABEZADO
    ws['!cols'] = esEfectivo
      ? [{ wch: 11 }, { wch: 34 }, { wch: 16 }]
      : [{ wch: 11 }, { wch: 34 }, { wch: 20 }, { wch: 22 }, { wch: 16 }]

    XLSX.utils.book_append_sheet(wb, ws, nombreHoja(titulo))
  })

  return {
    wb,
    nombreArchivo: `dispersion_${limpiarNombre(prenomina.nombre)}.xlsx`,
  }
}

/** Envoltura de navegador de construirLibroDispersion. */
export async function descargarDispersion({ prenomina, bancosPorId, noSumaIds, especieIds }) {
  const png = await cargarLogoBiwo()
  const conLogo = !!png

  const { wb, nombreArchivo } =
    construirLibroDispersion({ prenomina, bancosPorId, noSumaIds, especieIds, conLogo })

  if (!conLogo) return XLSX.writeFile(wb, nombreArchivo)

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  try {
    saveAs(await inyectarLogo(buffer, png, wb.SheetNames.length), nombreArchivo)
  } catch (err) {
    console.error('[descargarDispersion] no se pudo insertar el logo', err)
    XLSX.writeFile(wb, nombreArchivo)
  }
}

// ─── Logo de BIWO dentro del .xlsx ────────────────────────────────────────────
// xlsx-js-style no sabe escribir imagenes (no tiene addImage ni escribe xl/media;
// eso es de SheetJS Pro), asi que el logo se inyecta a mano en el zip que produce:
// la imagen, un drawing por hoja y sus relaciones.
//
// No sirve Components/ExcelLogo.js: ese solo parcha sheet1/drawing1 y aqui las hojas
// son varias.
export async function cargarLogoBiwo() {
  try {
    const res = await fetch(logoHorizontal)
    if (!res.ok) return null
    return new Uint8Array(await res.arrayBuffer())
  } catch {
    return null   // sin logo se cae al encabezado de texto
  }
}

// El PNG mide 812x300; se dibuja a 152x56 px para que quepa en las dos primeras
// filas conservando la proporcion. Excel mide en EMU: 1 px = 9525.
function xmlDrawingLogo() {
  const EMU = 9525
  const cx = 152 * EMU
  const cy = 56 * EMU
  const off = 2 * EMU
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"'
    + ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">'
    + '<xdr:oneCellAnchor>'
    + `<xdr:from><xdr:col>0</xdr:col><xdr:colOff>${off}</xdr:colOff>`
    + `<xdr:row>0</xdr:row><xdr:rowOff>${off}</xdr:rowOff></xdr:from>`
    + `<xdr:ext cx="${cx}" cy="${cy}"/>`
    + '<xdr:pic><xdr:nvPicPr>'
    + '<xdr:cNvPr id="1" name="BIWO" descr="BIWO"/>'
    + '<xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr>'
    + '</xdr:nvPicPr><xdr:blipFill>'
    + '<a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rId1"/>'
    + '<a:stretch><a:fillRect/></a:stretch></xdr:blipFill>'
    + `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>`
    + '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>'
    + '</xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>'
}

/**
 * Mete el logo en cada hoja del .xlsx ya generado y devuelve el Blob final.
 * @param {ArrayBuffer} buffer  el xlsx que escribio xlsx-js-style
 * @param {Uint8Array}  png     bytes del logo
 * @param {number}      hojas   cuantas hojas llevan logo (1..n, en orden)
 */
export async function inyectarLogo(buffer, png, hojas) {
  const zip = await JSZip.loadAsync(buffer)
  const RELS = 'http://schemas.openxmlformats.org/package/2006/relationships'
  const drawing = xmlDrawingLogo()

  // createFolders:false deja el paquete igual que lo entrega SheetJS, sin entradas
  // de directorio que el no escribe.
  const OPC = { createFolders: false }
  const poner = (ruta, contenido) => zip.file(ruta, contenido, OPC)

  poner('xl/media/image1.png', png)

  // [Content_Types].xml ya trae el Default de png; solo faltan los drawings
  let ct = await zip.file('[Content_Types].xml').async('string')
  let overrides = ''

  const RELS_DOC = RELS.replace('/package/2006', '/officeDocument/2006')

  for (let h = 1; h <= hojas; h++) {
    poner(`xl/drawings/drawing${h}.xml`, drawing)
    poner(
      `xl/drawings/_rels/drawing${h}.xml.rels`,
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + `<Relationships xmlns="${RELS}"><Relationship Id="rId1"`
      + ` Type="${RELS_DOC}/image" Target="../media/image1.png"/></Relationships>`
    )
    // La hoja no traia _rels propio, asi que se crea con la unica relacion
    poner(
      `xl/worksheets/_rels/sheet${h}.xml.rels`,
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + `<Relationships xmlns="${RELS}"><Relationship Id="rId1"`
      + ` Type="${RELS_DOC}/drawing" Target="../drawings/drawing${h}.xml"/></Relationships>`
    )
    overrides += `<Override PartName="/xl/drawings/drawing${h}.xml"`
      + ' ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>'

    // <drawing> va al final del worksheet (despues de ignoredErrors, segun el
    // esquema). La raiz ya declara xmlns:r, pero se comprueba por si acaso.
    const ruta = `xl/worksheets/sheet${h}.xml`
    let xml = await zip.file(ruta).async('string')
    if (!/<worksheet[^>]*xmlns:r=/.test(xml)) {
      xml = xml.replace('<worksheet ', `<worksheet xmlns:r="${RELS_DOC}" `)
    }
    poner(ruta, xml.replace('</worksheet>', '<drawing r:id="rId1"/></worksheet>'))
  }

  poner('[Content_Types].xml', ct.replace('</Types>', `${overrides}</Types>`))

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

// ─── Datos por empleado (hoja plana para calculos externos) ──────────────────

// Nombre de la hoja y encabezados fijos. Son un CONTRATO: la "Calculadora de Asimilables" que
// usa el cliente fuera del sistema busca la hoja por este nombre y cada columna por el texto de
// su encabezado. Cambiar uno de estos textos rompe su macro. Se pueden AGREGAR columnas (la macro
// ignora las que no conoce), pero no renombrar ni quitar.
//
// Despues de los fijos van `Día 1` ... `Día N`, una por dia del periodo con la marca de
// `marcasDiaDeLinea` (numero = faltas, MARCA_INCAPACIDAD, MARCA_VACACIONES, MARCA_FUERA o la clave
// del catalogo). La calculadora pinta la semana con ellas y la rejilla MANDA en su calculo: cuenta
// las faltas, las incapacidades y los dias fuera directamente de esas celdas.
//
// `Registro patronal` (el del empleado: una prenomina puede mezclar varios), `Razón social` y
// `RFC empresa` van al final de los fijos: la calculadora arma una hoja de calculo por registro
// patronal y los pone en el encabezado de cada hoja.
export const HOJA_DATOS_POR_EMPLEADO = 'Datos por empleado'
const ENCABEZADOS_FIJOS_DATOS = [
  'Periodo', 'Fecha inicio', 'Fecha fin', 'Código', 'Empleado', 'Departamento', 'Núm. departamento',
  'Banco', 'Cuenta', 'Estatus', 'Fecha baja', 'Días del periodo', 'Días falta', 'Días incapacidad',
  'Días fuera de relación', 'Horas retardo', 'TOTAL PERCEPCIONES', 'TOTAL DEDUCCIONES', 'NETO',
  'Registro patronal', 'Razón social', 'RFC empresa',
]
const encabezadoDia = (diaPeriodo) => `Día ${diaPeriodo}`

const redondear2 = (v) => Math.round((Number(v) || 0) * 100) / 100

const fechaUtcTexto = (fecha) => {
  const t = anclarDesdeUtc(fecha)
  if (t === null) return ''
  const d = new Date(t)
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getUTCFullYear()}`
}

/**
 * Libro de UNA hoja, plano: un renglon por empleado y los encabezados en la fila 1. Sin logo, sin
 * banners, sin merges y sin renglones de total — es para que lo lea una macro, no una persona.
 *
 * Existe porque el asimilable que algunos clientes pagan por fuera de la nomina se calcula en un
 * Excel suyo (la "Calculadora de Asimilables") y NO puede vivir en el ERP: aqui solo sale lo
 * fiscal. La clasificacion de que deduccion se le cobra al empleado y que percepcion es un extra
 * tambien vive alla, porque cambia de una empresa a otra.
 *
 * Las columnas de concepto van rotuladas `P: ` / `D: ` + descripcion: sin el prefijo chocarian
 * nombres como "Prestamo empresa", que en algunos catalogos existe como percepcion (la entrega) y
 * como deduccion (el cobro). Quedan fuera los informativos, las percepciones en especie y las
 * obligaciones, que no tocan el NETO.
 *
 * Los dias salen de `resumenDiasDeLinea`, con las mismas reglas que la rejilla de la Lista de Raya
 * con Incidencias, asi que "Dias falta" es exactamente su TOTAL FALTAS.
 *
 * Las lineas de FINIQUITO (`finiquitoId`) no salen: el asimilable es de la nomina ordinaria y el
 * cliente no quiere que un finiquito llegue a su calculadora (ni como baja ni como empleado).
 *
 * @param {Object} prenomina    con `lineas` (de datosPrenomina: trae la ventana laboral y la baja)
 * @param {Object} indice       salida de `construirIndiceTipos`
 * @param {Object} bancosPorId  catalogo de bancos por id, como en la dispersion
 */
export function construirLibroDatosPorEmpleado({ prenomina, noSumaIds, especieIds, indice, bancosPorId = {} }) {
  const lineas = (prenomina?.lineas || []).filter(l => !l.finiquitoId)
  if (lineas.length === 0) throw new Error('La prenómina no tiene líneas fuera de los finiquitos')

  const dias = construirDiasPeriodo(prenomina)
  const catalogo = construirCatalogoConceptos(lineas, noSumaIds, especieIds)
  const percs = catalogo.percs.filter(c => !c.noSuma && !c.especie)
  const dedus = catalogo.dedus.filter(c => !c.noSuma)

  // Rotulos unicos por tipo: si dos conceptos se llaman igual se desempatan con su serie-numero.
  const rotular = (conceptos, prefijo) => {
    const cuenta = new Map()
    conceptos.forEach(c => {
      const k = (c.descripcion || '').trim().toUpperCase()
      cuenta.set(k, (cuenta.get(k) || 0) + 1)
    })
    return conceptos.map(c => {
      const desc = (c.descripcion || '').trim()
      const repetido = cuenta.get(desc.toUpperCase()) > 1
      return `${prefijo}: ${desc}${repetido ? ` (${c.serie}${c.numero})` : ''}`
    })
  }
  const rotulosP = rotular(percs, 'P')
  const rotulosD = rotular(dedus, 'D')

  const periodo = prenomina.periodoNumero != null ? String(prenomina.periodoNumero) : ''
  const fIni = fechaUtcTexto(prenomina.fechaInicio)
  const fFin = fechaUtcTexto(prenomina.fechaFin)
  const razonSocial = (prenomina.empresaNombre || '').trim()
  const rfcEmpresa = (prenomina.empresaRfc || '').trim()

  const nombreBanco = (l) => {
    const id = l.bancoPagoElectronicoId
    const banco = id ? bancosPorId[id.toString()] : null
    return banco ? (banco.nombre || banco.banco || '') : ''
  }

  const ordenadas = lineas.slice().sort((a, b) => {
    const da = a.departamentoNumero == null ? 0 : a.departamentoNumero
    const db = b.departamentoNumero == null ? 0 : b.departamentoNumero
    if (da !== db) return da - db
    const na = (a.departamentoNombre || '').localeCompare(b.departamentoNombre || '')
    return na !== 0 ? na : porCodigo(a, b)
  })

  const filas = [[
    ...ENCABEZADOS_FIJOS_DATOS.map(t => cs(t, hdrTxt())),
    ...dias.map(d => cs(encabezadoDia(d.diaPeriodo), hdrDia(d.esSeptimo))),
    ...rotulosP.map(t => cs(t, hdr())),
    ...rotulosD.map(t => cs(t, hdr())),
  ]]

  ordenadas.forEach((l, i) => {
    const impar = i % 2 === 1
    const percVals = percs.map(c => getVal(l, c.id))
    const deduVals = dedus.map(c => getVal(l, c.id))
    // Misma regla que crearFilaDeLinea: sin informativos ni especie, que aqui ya se filtraron.
    // Redondeados a centavos: la macro los compara y resta, y un 5449.389999 se ve como error.
    const totalP = redondear2(percVals.reduce((s, v) => s + v, 0))
    const totalD = redondear2(deduVals.reduce((s, v) => s + v, 0))
    const neto = redondear2(totalP - totalD)
    const d = resumenDiasDeLinea(l, dias, indice)
    const marcas = marcasDiaDeLinea(l, dias, indice)
    const codigo = (l.codigo === null || l.codigo === undefined || l.codigo === '')
      ? '' : String(l.codigo).padStart(4, '0')

    filas.push([
      cs(periodo, sCod(impar)),
      cs(fIni, sFecha(impar)),
      cs(fFin, sFecha(impar)),
      cs(codigo, sCod(impar)),
      cs([l.apellidoPaterno, l.apellidoMaterno, l.nombre].map(p => (p || '').trim()).filter(Boolean).join(' '), sNom(impar)),
      cs(l.departamentoNombre || 'SIN DEPARTAMENTO', sNom(impar)),
      cn(l.departamentoNumero == null ? 0 : l.departamentoNumero, sEnteros(impar)),
      cs(nombreBanco(l), sNom(impar)),
      cs((l.numCuenta || l.clabeInterbancaria || '').trim(), sCod(impar)),
      cs(l.esBajaEnPeriodo ? 'BAJA' : 'ACTIVO', sFecha(impar)),
      cs(l.fechaBajaTexto || '', sFecha(impar)),
      cn(dias.length, sEnteros(impar)),
      cn(d.faltas, sEnteros(impar)),
      cn(d.incapacidades, sEnteros(impar)),
      cn(d.fueraDeRelacion, sEnteros(impar)),
      cn(d.horasRetardo, sDiaNum(impar)),
      cn(totalP, sImp(totalP, false, impar)),
      cn(totalD, sImp(totalD, false, impar)),
      cn(neto, sImp(neto, false, impar)),
      cs((l.registroPatronalIMSS || '').trim(), sCod(impar)),
      cs(razonSocial, sNom(impar)),
      cs(rfcEmpresa, sCod(impar)),
      ...marcas.map(m => (typeof m === 'number'
        ? cn(m, sDiaNum(impar))
        : cs(m, sDia(impar, null)))),
      ...percVals.map(v => cn(v, sImp(v, false, impar))),
      ...deduVals.map(v => cn(v, sImp(v, false, impar))),
    ])
  })

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!rows'] = [{ hpt: 34 }]
  ws['!cols'] = [
    { wch: 8 }, { wch: 11 }, { wch: 11 }, { wch: 9 }, { wch: 34 }, { wch: 24 }, { wch: 10 }, { wch: 16 },
    { wch: 20 }, { wch: 9 }, { wch: 11 }, { wch: 9 }, { wch: 9 }, { wch: 11 }, { wch: 11 }, { wch: 9 },
    { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 34 }, { wch: 15 },
    ...dias.map(() => ({ wch: 7 })),
    ...Array.from({ length: percs.length + dedus.length }, () => ({ wch: 15 })),
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, HOJA_DATOS_POR_EMPLEADO)
  return { wb, nombreArchivo: `datos_empleados_${limpiarNombre(prenomina.nombre)}.xlsx` }
}

/** Envoltura de navegador de `construirLibroDatosPorEmpleado`. Sin logo: la hoja la lee una macro. */
export function descargarDatosPorEmpleado(params) {
  const { wb, nombreArchivo } = construirLibroDatosPorEmpleado(params)
  XLSX.writeFile(wb, nombreArchivo)
}

/**
 * Envoltura de navegador: carga el logo, arma el libro, le mete la imagen y dispara
 * la descarga.
 */
export async function descargarListaRaya({ prenominas, noSumaIds, especieIds, opciones = {}, nombreArchivo }) {
  // El logo se resuelve antes de armar las hojas: de eso depende si las dos primeras
  // filas se dejan libres para la imagen o llevan el texto de respaldo.
  const png = await cargarLogoBiwo()
  const conLogo = !!png

  const { wb, nombreArchivo: nombreCalculado } =
    construirLibroListaRaya({ prenominas, noSumaIds, especieIds, conLogo, opciones })

  const archivo = nombreArchivo || nombreCalculado

  // Sin logo se descarga tal cual; con logo hay que reabrir el zip para meterlo
  if (!conLogo) return XLSX.writeFile(wb, archivo)

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  try {
    // El numero de hojas se deriva del workbook, no se escribe a mano: en el modo por
    // proyecto son N+1 y con un 2 fijo el logo solo salia en las dos primeras.
    saveAs(await inyectarLogo(buffer, png, wb.SheetNames.length), archivo)
  } catch (err) {
    // Si algo falla al inyectar, mejor entregar el Excel sin logo que nada
    console.error('[descargarListaRaya] no se pudo insertar el logo', err)
    XLSX.writeFile(wb, archivo)
  }
}
